import React, { useState, useEffect } from "react";
import { MessageCircle, Send, CheckCheck, Bell, Users } from "lucide-react";
import { whatsappApi, donationsApi } from "../utils/api";

const templates = [
  {
    id: "receipt",
    name: "Donation Receipt",
    icon: "🧾",
    description: "Send receipt confirmation after donation",
    message: "🕉️ *Sri Siddhi Vinayaka Utsava Samithi*\n\nNamaste {name},\n\nThank you for your generous donation of *{amount}* towards Ganesh Chaturthi 2026.\n\nReceipt No: *{receipt}*\nDate: {date}\n\n🙏 Ganapati Bappa Morya!\n\n_utsav.btrix.in_",
    variables: ["name", "amount", "receipt", "date"],
  },
  {
    id: "event",
    name: "Event Invitation",
    icon: "🎭",
    description: "Invite members to upcoming events",
    message: "🕉️ *Sri Siddhi Vinayaka Utsava Samithi*\n\nDear {name},\n\nYou are cordially invited to *{event_name}*\n\n📅 Date: {date}\n⏰ Time: {time}\n📍 Venue: {venue}\n\nYour presence will make this celebration special.\n\n🙏 Ganapati Bappa Morya!",
    variables: ["name", "event_name", "date", "time", "venue"],
  },
  {
    id: "announcement",
    name: "Festival Announcement",
    icon: "📢",
    description: "General festival announcements",
    message: "🕉️ *Ganesh Chaturthi 2026*\n\n*Sri Siddhi Vinayaka Utsava Samithi* is pleased to announce that preparations for Ganesh Chaturthi 2026 are in full swing!\n\n🗓️ Festival: Aug 22 - Sep 1, 2026\n📍 Venue: Bengaluru\n\nJoin us for 11 days of celebrations!\n\n🙏 Ganapati Bappa Morya!",
    variables: [],
  },
  {
    id: "volunteer",
    name: "Volunteer Duty",
    icon: "👥",
    description: "Remind volunteers about their duties",
    message: "🕉️ Dear *{name}*,\n\nThis is a reminder for your volunteer duty:\n\n📋 Department: *{dept}*\n🕐 Shift: *{shift}*\n📅 Date: {date}\n\nPlease report 15 minutes before your shift.\n\nThank you for your service!\n\n_Sri Siddhi Vinayaka Utsava Samithi_",
    variables: ["name", "dept", "shift", "date"],
  },
];

const mockMessages = [
  { id: 1, to: "Ramesh Kumar", phone: "9876543210", template: "Donation Receipt", status: "delivered", time: "10:32 AM", type: "receipt" },
  { id: 2, to: "Sunita Sharma", phone: "9871234560", template: "Donation Receipt", status: "delivered", time: "10:35 AM", type: "receipt" },
  { id: 3, to: "All Members", phone: "Group", template: "Festival Announcement", status: "sent", time: "11:00 AM", type: "announcement" },
  { id: 4, to: "Vijay Patel", phone: "9865432109", template: "Donation Receipt", status: "read", time: "02:15 PM", type: "receipt" },
  { id: 5, to: "Arun Kumar", phone: "9876543210", template: "Volunteer Duty", status: "delivered", time: "03:00 PM", type: "volunteer" },
];

const statusIcons = {
  read: <CheckCheck size={12} className="text-blue-500" />,
  delivered: <CheckCheck size={12} className="text-green-500" />,
  sent: <CheckCheck size={12} className="text-gray-400" />,
  failed: <span className="text-red-500 text-[10px]">Failed</span>,
};

export default function WhatsApp() {
  const [donors, setDonors] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [recipients, setRecipients] = useState("individual");
  const [selectedDonor, setSelectedDonor] = useState("");
  const [messages, setMessages] = useState(mockMessages);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    donationsApi.list()
      .then((res) => setDonors(res.donations || []))
      .catch(console.error);
  }, []);

  const handleSend = async () => {
    if (!selectedTemplate) return;
    setSending(true);

    const recipientList = recipients === "all"
      ? donors.map((d) => ({ name: d.donorName, phone: d.phone }))
      : selectedDonor
        ? [{ name: selectedDonor.split(" — ")[0], phone: selectedDonor.split(" — ")[1] }]
        : [];

    try {
      await whatsappApi.send({
        type: selectedTemplate.id,
        recipients: recipientList,
        message: selectedTemplate.message,
        committeeName: "Sri Siddhi Vinayaka Utsava Samithi",
      });
      setSent(true);
      setTimeout(() => setSent(false), 3000);
    } catch {
      // fall through — still add to log
    }

    setMessages((prev) => [{
      id: Date.now(),
      to: recipients === "all" ? `All Donors (${donors.length})` : (recipientList[0]?.name || "—"),
      phone: recipients === "all" ? "Group" : recipientList[0]?.phone || "—",
      template: selectedTemplate.name,
      status: "sent",
      time: new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }),
      type: selectedTemplate.id,
    }, ...prev.slice(0, 19)]);

    setSending(false);
  };

  return (
    <div className="p-4 lg:p-6 space-y-5 fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">WhatsApp Notifications</h1>
          <p className="text-sm text-gray-500">Powered by Gupshup API</p>
        </div>
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-3 py-1.5">
          <div className="w-2 h-2 bg-green-500 rounded-full pulse-orange" />
          <span className="text-xs font-semibold text-green-700">Connected</span>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Sent Today", value: messages.length, icon: Send, color: "text-blue-600 bg-blue-50" },
          { label: "Delivered", value: messages.filter((m) => m.status !== "failed").length, icon: CheckCheck, color: "text-green-600 bg-green-50" },
          { label: "Read", value: messages.filter((m) => m.status === "read").length, icon: Bell, color: "text-orange-600 bg-orange-50" },
          { label: "Recipients", value: donors.length, icon: Users, color: "text-purple-600 bg-purple-50" },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-white rounded-2xl p-3.5 border border-gray-100 shadow-sm">
              <div className={`w-8 h-8 rounded-xl flex items-center justify-center mb-2 ${s.color}`}>
                <Icon size={15} />
              </div>
              <p className="text-xl font-black text-gray-900">{s.value}</p>
              <p className="text-[11px] text-gray-500 font-medium">{s.label}</p>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Compose */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
          <h2 className="text-sm font-bold text-gray-900">Compose Message</h2>

          {/* Templates */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-2 block">Select Template</label>
            <div className="grid grid-cols-2 gap-2">
              {templates.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setSelectedTemplate(t)}
                  className={`p-3 text-left rounded-xl border transition-all ${
                    selectedTemplate?.id === t.id
                      ? "border-orange-400 bg-orange-50"
                      : "border-gray-200 hover:border-orange-200"
                  }`}
                >
                  <div className="text-xl mb-1">{t.icon}</div>
                  <p className="text-xs font-bold text-gray-800">{t.name}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5 line-clamp-1">{t.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Recipients */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-2 block">Send To</label>
            <div className="flex gap-2">
              {["individual", "all"].map((r) => (
                <button
                  key={r}
                  onClick={() => setRecipients(r)}
                  className={`flex-1 py-2 rounded-xl text-xs font-semibold capitalize transition-colors ${
                    recipients === r ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {r === "individual" ? "Individual" : "All Donors"}
                </button>
              ))}
            </div>
          </div>

          {recipients === "individual" && (
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Select Donor</label>
              <select value={selectedDonor} onChange={(e) => setSelectedDonor(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400">
                <option value="">Choose donor...</option>
                {donors.map((d) => (
                  <option key={d.id} value={`${d.donorName} — ${d.phone}`}>{d.donorName} — {d.phone}</option>
                ))}
              </select>
            </div>
          )}

          {/* Preview */}
          {selectedTemplate && (
            <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
              <p className="text-[10px] font-semibold text-gray-400 uppercase mb-1.5">Message Preview</p>
              <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">
                {selectedTemplate.message.substring(0, 250)}...
              </pre>
            </div>
          )}

          <button
            onClick={handleSend}
            disabled={!selectedTemplate || sending}
            className={`w-full flex items-center justify-center gap-2 font-bold py-3 rounded-xl transition-all ${
              sent
                ? "bg-green-500 text-white"
                : selectedTemplate
                ? "bg-orange-500 hover:bg-orange-600 text-white shadow-lg shadow-orange-500/20"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            }`}
          >
            {sending ? (
              <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Sending...</>
            ) : sent ? (
              <><CheckCheck size={16} /> Message Sent!</>
            ) : (
              <><Send size={16} /> Send via Gupshup</>
            )}
          </button>
        </div>

        {/* Message Log */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <h2 className="text-sm font-bold text-gray-900 mb-4">Message Log</h2>
          <div className="space-y-2.5">
            {messages.map((msg) => (
              <div key={msg.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <MessageCircle size={14} className="text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-0.5">
                    <p className="text-xs font-bold text-gray-800 truncate">{msg.to}</p>
                    <span className="text-[10px] text-gray-400 flex-shrink-0 ml-2">{msg.time}</span>
                  </div>
                  <p className="text-[10px] text-gray-500">{msg.template}</p>
                </div>
                <div className="flex-shrink-0">{statusIcons[msg.status]}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
